import { store } from "@store/index";
import { type SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  type PageV2Component,
  type SideNavigationV2Props,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { createCommunicationChannel } from "@store/features/chat/chatSlice";
import { updateCommunicationChannelField } from "@store/features/apps/appSlice";
import type { ChangeEvent } from "react";
import { SpanCol } from "skedyul-ui/ui";

type AddChannelSidebarParams = {
  subdomain: string;
  phoneNumbers: { id: string; phone: string }[];
  name: string;
  identifierValue: string;
  incomingWorkflowId?: string;
  outgoingWorkflowId?: string;
  identifierMode?: "DEDICATED_PHONE" | "TEXT";
  definitionHandle?: string;
  channelSettings?: Array<{
    handle: string;
    label: string;
    description: string;
    icon: string;
    placeholder: string;
    required: boolean;
    variableKey: string;
    parenthandle?: string;
    order: number;
    control: {
      type: "input" | "text-area" | "select" | "checkbox";
      dataType: "string" | "number" | "boolean" | "date" | "json";
      options?: Array<{ label: string; value: string }>;
    };
  }>;
  settingsValues?: Record<string, unknown>;
};

export const addChannelViewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: AddChannelSidebarParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const {
    apps: {
      communicationChannel: { data: channel },
    },
    sideNavigation: {
      right: { isOpen },
    },
  } = args.state;

  const channelState = channel || {
    name: "",
    identifierValue: "",
    identifierMode: undefined as "DEDICATED_PHONE" | "TEXT" | undefined,
    definitionHandle: undefined as string | undefined,
    settingsValues: {} as Record<string, unknown>,
    channelSettings: [] as NonNullable<
      AddChannelSidebarParams["channelSettings"]
    >,
    phoneNumbers: [] as { id: string; phone: string }[],
  };

  // Derive a friendly type name to personalize copy
  const channelTypeName = (() => {
    if (channelState.identifierMode === "DEDICATED_PHONE")
      return "Phone Number";
    if (channelState.definitionHandle === "email") return "Email";
    return "Account";
  })();

  const updateParams = (next: Partial<AddChannelSidebarParams>) => {
    if (next.name !== undefined) {
      store.dispatch(
        updateCommunicationChannelField({ key: "name", value: next.name }),
      );
    }
    if (next.identifierValue !== undefined) {
      store.dispatch(
        updateCommunicationChannelField({
          key: "identifierValue",
          value: next.identifierValue,
        }),
      );
    }
    if (next.identifierMode !== undefined) {
      store.dispatch(
        updateCommunicationChannelField({
          key: "identifierMode",
          value: next.identifierMode,
        }),
      );
    }
    if (next.definitionHandle !== undefined) {
      store.dispatch(
        updateCommunicationChannelField({
          key: "definitionHandle",
          value: next.definitionHandle,
        }),
      );
    }
    if (next.settingsValues !== undefined) {
      store.dispatch(
        updateCommunicationChannelField({
          key: "settingsValues",
          value: next.settingsValues,
        }),
      );
    }
  };

  const getParentValue = (parentHandle?: string) => {
    if (!parentHandle) return undefined;
    const parent = (channelState.channelSettings || []).find(
      (s) => s.handle === parentHandle,
    );
    if (!parent) return undefined;
    return (channelState.settingsValues || {})[parent.variableKey];
  };

  const isHiddenByParent = (parentHandle?: string) => {
    if (!parentHandle) return false;
    const val = getParentValue(parentHandle);
    return !val;
  };

  const dynamicFields = (channelState.channelSettings || [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((setting) => {
      const currentValue = (channelState.settingsValues || {})[
        setting.variableKey
      ];
      const common = {
        span: 12 as const,
        mdSpan: 12 as const,
        lgSpan: 12 as const,
        hidden: isHiddenByParent(setting.parenthandle),
      };

      switch (setting.control.type) {
        case "checkbox":
          return {
            component: "Checkbox" as const,
            ...common,
            props: {
              id: setting.handle,
              label: setting.label,
              checked: Boolean(currentValue ?? false),
              helpText: setting.description,
              handleChange: (checked: boolean) =>
                updateParams({
                  settingsValues: {
                    ...(channelState.settingsValues || {}),
                    [setting.variableKey]: checked,
                  },
                }),
            },
          };
        case "select":
          return {
            component: "Select" as const,
            ...common,
            props: {
              id: setting.handle,
              label: setting.label,
              value: (currentValue as string) || "",
              placeholder: setting.placeholder,
              items:
                setting.control.options?.map((o) => ({
                  value: o.value,
                  label: o.label,
                })) || [],
              handleChange: (value: string) =>
                updateParams({
                  settingsValues: {
                    ...(channelState.settingsValues || {}),
                    [setting.variableKey]: value,
                  },
                }),
            },
          };
        case "text-area":
          return {
            component: "Textarea" as const,
            ...common,
            props: {
              id: setting.handle,
              label: setting.label,
              value: (currentValue as string) || "",
              placeholder: setting.placeholder,
              handleChange: (e: ChangeEvent<HTMLTextAreaElement>) =>
                updateParams({
                  settingsValues: {
                    ...(channelState.settingsValues || {}),
                    [setting.variableKey]: e.target.value,
                  },
                }),
            },
          };
        case "input":
        default: {
          if (setting.control.dataType === "date") {
            return {
              component: "DatePicker" as const,
              ...common,
              props: {
                id: setting.handle,
                placeholder: setting.placeholder,
                label: setting.label,
                value: (currentValue
                  ? new Date(String(currentValue))
                  : undefined) as unknown as Date | undefined,
                handleChange: (date?: Date) =>
                  updateParams({
                    settingsValues: {
                      ...(channelState.settingsValues || {}),
                      [setting.variableKey]: date ? date.toISOString() : "",
                    },
                  }),
              },
            };
          }

          const inputType: "text" | "number" =
            setting.control.dataType === "number" ? "number" : "text";
          return {
            component: "Input" as const,
            ...common,
            props: {
              type: inputType,
              id: setting.handle,
              label: setting.label,
              value:
                inputType === "number"
                  ? (currentValue as number | string) ?? ""
                  : (currentValue as string) || "",
              placeholder: setting.placeholder,
              handleChange: (e: ChangeEvent<HTMLInputElement>) =>
                updateParams({
                  settingsValues: {
                    ...(channelState.settingsValues || {}),
                    [setting.variableKey]:
                      inputType === "number"
                        ? e.target.value === ""
                          ? ""
                          : Number(e.target.value)
                        : e.target.value,
                  },
                }),
            },
          };
        }
      }
    });

  // Entity mapping removed: appModels are install-scoped only. Sidebar focuses on channel config.

  return {
    side: "right",
    isOpen,
    width: "700px",
    component: {
      component: "PageV2",
      props: {
        title: `Add ${channelTypeName}`,
        hideTitle: true,
        header: {
          icon:
            channelState.identifierMode === "DEDICATED_PHONE"
              ? "Phone"
              : "Mail",
          primaryAction: {
            label: `Create ${channelTypeName}`,
            variant: "default",
            size: "sm",
            isDisabled: !channelState?.name || !channelState?.identifierValue,
            handleClick: async () => {
              const appInstallationId =
                store.getState().apps.appInstall.data?.installation?.id;
              if (!appInstallationId) {
                return;
              }
              const action = await store.dispatch(
                createCommunicationChannel({
                  name: channelState.name || "",
                  identifierValue: channelState.identifierValue || "",
                  // Channel handle from executable.config (skedyul.config.ts)
                  handle: channelState?.handle || "",
                  appInstallationId,
                  linkedFields: Array.isArray(
                    (
                      channelState as unknown as {
                        linkedFields?: Array<{ id: string }>;
                      }
                    )?.linkedFields,
                  )
                    ? (
                        channelState as unknown as {
                          linkedFields: Array<{ id: string }>;
                        }
                      ).linkedFields.map((f) => f.id)
                    : [],
                }),
              );

              if (action.meta.requestStatus === "fulfilled") {
                void store.dispatch(closeSideNavigation({ position: "right" }));
              }
            },
          },
        },
        content: {
          structure: "object",
          components: [
            {
              type: "Card" as const,
              title: `Add ${channelTypeName}`,
              props: {
                header: {
                  title: `Add ${channelTypeName}`,
                  description: `Create a ${channelTypeName.toLowerCase()} for this app`,
                },
                secondaryActions: [],
                form: {
                  formVersion: "v1" as const,
                  id: "add-channel-form",
                  fields: [
                    {
                      component: "Input" as const,
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text" as const,
                        id: "channel-name",
                        label: "Name",
                        leftIcon: "Tag",
                        value: channelState?.name || "",
                        placeholder:
                          "After hours phone, Emergency Room, Front Desk etc.",
                        handleChange: (e: ChangeEvent<HTMLInputElement>) =>
                          updateParams({ name: e.target.value }),
                      },
                    },
                    ...(channelState?.identifierMode === "DEDICATED_PHONE"
                      ? [
                          {
                            component: "Select" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              id: "channel-identifier",
                              label: "Phone",
                              leftIcon: "Phone" as const,
                              value: channelState?.identifierValue || "",
                              placeholder: "Select a phone number",
                              items: (channelState?.phoneNumbers || []).map(
                                (ph) => ({
                                  value: ph.phone,
                                  label: ph.phone,
                                }),
                              ),
                              handleChange: (value: string) =>
                                updateParams({ identifierValue: value }),
                            },
                          },
                        ]
                      : [
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              type: "email" as const,
                              id: "channel-identifier",
                              label: "Email",
                              leftIcon: "Mail" as const,
                              value: channelState?.identifierValue || "",
                              placeholder: "name@example.com",
                              handleChange: (
                                e: ChangeEvent<HTMLInputElement>,
                              ) =>
                                updateParams({
                                  identifierValue: e.target.value,
                                }),
                            },
                          },
                        ]),
                    ...(dynamicFields || []),
                  ],
                },
              },
            },
            // Entity cards removed with appModels move to install scope
          ],
        },
      },
    },
  };
};
